class AuthorArticleFilters extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-author-filter]'));
    this.searchInput = this.querySelector('[data-author-search]');
    this.articleContainer = document.querySelector('[data-author-articles]');
    this.emptyState = document.querySelector('[data-author-articles-empty]');
    this.section = this.closest('.shopify-section');
    this.sectionId = this.section?.id?.replace('shopify-section-', '');
    this.filterBar = this.querySelector('.author-filter-bar');
    this.scroller = this.querySelector('[data-author-filter-scroller]');
    this.track = this.querySelector('[data-author-filter-track]');
    this.prevButton = this.querySelector('[data-author-filter-prev]');
    this.nextButton = this.querySelector('[data-author-filter-next]');
    this.abortController = null;
    this.selectedFilter = '';
    this.handleScrollerResize = this.handleScrollerResize || (() => this.syncScrollerState());
    this.handleScrollerScroll = this.handleScrollerScroll || (() => this.syncScrollerState());

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => {
        this.selectedFilter = button.dataset.authorFilter || '';
        this.updateActiveButton(button);
        this.fetchAndUpdateArticles();
      });
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.fetchAndUpdateArticles());
      this.searchInput.form?.addEventListener('submit', (event) => event.preventDefault());
      this.searchInput.form?.addEventListener('reset', () => {
        setTimeout(() => this.fetchAndUpdateArticles());
      });
    }

    this.buttons.forEach((button) => {
      button.setAttribute('aria-pressed', button.classList.contains('active') ? 'true' : 'false');
    });
    this.setupScroller();
    this.updateEmptyState(Array.from(document.querySelectorAll('[data-author-article]')).length);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleScrollerResize);
    this.scroller?.removeEventListener('scroll', this.handleScrollerScroll);
  }

  updateActiveButton(activeButton) {
    this.buttons.forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.revealButton(activeButton);
  }

  setupScroller() {
    if (!this.scroller || !this.track || !this.prevButton || !this.nextButton) return;

    this.prevButton.addEventListener('click', () => this.scrollFilters('prev'));
    this.nextButton.addEventListener('click', () => this.scrollFilters('next'));
    this.scroller.addEventListener('scroll', this.handleScrollerScroll, { passive: true });
    window.addEventListener('resize', this.handleScrollerResize);
    this.syncBreakout();
    this.syncScrollerState();
  }

  syncBreakout() {
    if (!this.filterBar) return;

    const parentRect = this.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const breakoutStart = Math.max(0, parentRect.left);
    const breakoutEnd = Math.max(0, viewportWidth - parentRect.right);

    this.filterBar.style.setProperty('--blog-filter-breakout-start', `${breakoutStart}px`);
    this.filterBar.style.setProperty('--blog-filter-breakout-end', `${breakoutEnd}px`);
  }

  getScrollTarget(direction) {
    if (!this.scroller) return 0;

    const offsets = this.buttons.map((button) => button.offsetLeft);
    const firstOffset = offsets[0] ?? 0;
    const normalizedOffsets = offsets.map((offset) => Math.max(0, offset - firstOffset));
    const currentPosition = this.scroller.scrollLeft;

    if (direction === 'prev') {
      return [...normalizedOffsets].reverse().find((offset) => offset < currentPosition - 2) ?? 0;
    }

    return normalizedOffsets.find((offset) => offset > currentPosition + 2) ?? currentPosition;
  }

  scrollFilters(direction) {
    if (!this.scroller) return;

    this.scroller.scrollTo({
      left: this.getScrollTarget(direction),
      behavior: 'smooth'
    });
  }

  getScrollerInsets() {
    if (!this.scroller) {
      return {
        left: 0,
        right: 0
      };
    }

    const styles = window.getComputedStyle(this.scroller);
    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    const prevInset = isDesktop && !this.prevButton?.hidden
      ? this.prevButton.parentElement?.getBoundingClientRect().width || 0
      : 0;
    const nextInset = isDesktop && !this.nextButton?.hidden
      ? this.nextButton.parentElement?.getBoundingClientRect().width || 0
      : 0;

    return {
      left: (parseFloat(styles.paddingLeft) || 0) + prevInset,
      right: (parseFloat(styles.paddingRight) || 0) + nextInset
    };
  }

  revealButton(button) {
    if (!this.scroller || !button) return;

    window.requestAnimationFrame(() => {
      this.syncScrollerState();

      const scrollerRect = this.scroller.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const { left: leftInset, right: rightInset } = this.getScrollerInsets();
      const visibleLeft = scrollerRect.left + leftInset;
      const visibleRight = scrollerRect.right - rightInset;
      const maxScrollLeft = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
      let targetScrollLeft = this.scroller.scrollLeft;

      if (buttonRect.left < visibleLeft) {
        targetScrollLeft += buttonRect.left - visibleLeft;
      } else if (buttonRect.right > visibleRight) {
        targetScrollLeft += buttonRect.right - visibleRight;
      }

      targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));
      if (Math.abs(targetScrollLeft - this.scroller.scrollLeft) > 1) {
        this.scroller.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
      }

      this.syncScrollerState();
    });
  }

  syncScrollerState() {
    if (!this.filterBar || !this.scroller || !this.track || !this.prevButton || !this.nextButton) return;

    this.syncBreakout();

    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    const navOverlayWidth = isDesktop ? 48 : 0;
    const visibleWidth = this.scroller.clientWidth - navOverlayWidth;
    const hasOverflow = isDesktop && this.track.scrollWidth > visibleWidth + 1;
    const canScrollPrev = hasOverflow && this.scroller.scrollLeft > 2;
    const maxScrollLeft = this.scroller.scrollWidth - this.scroller.clientWidth - 2;
    const canScrollNext = hasOverflow && this.scroller.scrollLeft < maxScrollLeft;

    this.filterBar.classList.toggle('blog-filters--has-overflow', hasOverflow);
    this.filterBar.classList.toggle('blog-filters--can-scroll-prev', canScrollPrev);
    this.filterBar.classList.toggle('blog-filters--can-scroll-next', canScrollNext);

    this.prevButton.hidden = !canScrollPrev;
    this.nextButton.hidden = !canScrollNext;
  }

  async fetchAndUpdateArticles() {
    if (!this.articleContainer) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setLoading(true);

    try {
      const response = await fetch(this.getSectionUrl(), { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      this.updateArticlesGrid(await response.text());
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error filtering author articles:', error);
    } finally {
      this.setLoading(false);
    }
  }

  getSectionUrl() {
    const url = new URL(window.location.href);
    if (this.sectionId) url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  }

  updateArticlesGrid(html) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const fetchedArticles = Array.from(parsedDocument.querySelectorAll('[data-author-article]'));
    const query = (this.searchInput?.value || '').trim().toLowerCase();
    const filteredArticles = fetchedArticles.filter((article) => {
      const categories = (article.dataset.authorCategories || '').split(',').filter(Boolean);
      const title = (article.dataset.authorTitle || '').toLowerCase();
      const matchesFilter = !this.selectedFilter || categories.includes(this.selectedFilter);
      const matchesSearch = !query || title.includes(query);

      return matchesFilter && matchesSearch;
    });

    this.articleContainer.replaceChildren();
    filteredArticles.forEach((article) => {
      this.articleContainer.appendChild(document.importNode(article, true));
    });

    this.updateEmptyState(filteredArticles.length);
  }

  updateEmptyState(visibleCount) {
    if (this.emptyState) {
      this.emptyState.hidden = visibleCount > 0;
    }
  }

  setLoading(isLoading) {
    this.classList.toggle('loading', isLoading);
    this.articleContainer?.classList.toggle('loading', isLoading);
    this.articleContainer?.toggleAttribute('aria-busy', isLoading);
  }
}

class AuthorBioToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.content = this.querySelector('[data-author-bio-content]');

    if (!this.button || !this.content) return;

    this.fullContent = this.content.innerHTML;
    this.fullText = this.content.textContent.trim().replace(/\s+/g, ' ');
    this.handleResize = this.handleResize || (() => this.updateClampState());

    this.updateClampState();
    window.addEventListener('resize', this.handleResize);

    this.button.addEventListener('click', () => {
      const isExpanded = this.classList.toggle('is-expanded');
      if (isExpanded) {
        this.restoreButtonPosition();
        this.content.innerHTML = this.fullContent;
      } else {
        this.renderCollapsedText();
      }
      this.button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      this.button.textContent = isExpanded ? this.button.dataset.lessLabel : this.button.dataset.moreLabel;
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }

  updateClampState() {
    const wasExpanded = this.classList.contains('is-expanded');
    this.classList.remove('is-expanded', 'is-clampable');
    if (!wasExpanded) this.classList.remove('is-ready');
    this.restoreButtonPosition();
    this.content.innerHTML = this.fullContent;

    window.requestAnimationFrame(() => {
      const lineHeight = parseFloat(window.getComputedStyle(this.content).lineHeight) || 19.5;
      const clampHeight = lineHeight * 3;
      const previousDisplay = this.content.style.display;
      const previousMaxHeight = this.content.style.maxHeight;
      const previousOverflow = this.content.style.overflow;

      this.content.style.display = 'block';
      this.content.style.maxHeight = 'none';
      this.content.style.overflow = 'visible';

      const isClampable = this.content.scrollHeight > clampHeight + 1;

      this.content.style.display = previousDisplay;
      this.content.style.maxHeight = previousMaxHeight;
      this.content.style.overflow = previousOverflow;

      this.classList.toggle('is-clampable', isClampable);
      if (wasExpanded && isClampable) {
        this.classList.add('is-expanded');
      } else if (isClampable) {
        this.renderCollapsedText();
      }
      if (!this.classList.contains('is-expanded')) {
        this.button.setAttribute('aria-expanded', 'false');
        this.button.textContent = this.button.dataset.moreLabel;
      }
      this.classList.add('is-ready');
    });
  }

  renderCollapsedText() {
    if (!this.fullText) return;

    const words = this.fullText.split(' ');
    const lineHeight = parseFloat(window.getComputedStyle(this.content).lineHeight) || 19.5;
    let best = '';

    for (let index = words.length; index > 0; index -= 1) {
      const text = `${words.slice(0, index).join(' ')}... `;
      this.content.textContent = text;
      this.content.appendChild(this.button);
      const textRects = this.getTextRects();
      const lastTextRect = textRects[textRects.length - 1];
      const buttonRect = this.button.getBoundingClientRect();
      const isButtonOnLastLine = lastTextRect && Math.abs(buttonRect.top - lastTextRect.top) <= lineHeight * 0.4;
      const lineCount = this.getLineCount(textRects, buttonRect, lineHeight);

      if (lineCount <= 3 && isButtonOnLastLine) {
        best = text;
        break;
      }
    }

    this.content.textContent = best || `${words.slice(0, 12).join(' ')}... `;
    this.content.appendChild(this.button);
  }

  restoreButtonPosition() {
    if (this.button.parentElement === this.content) {
      this.content.after(this.button);
    }
  }

  getTextRects() {
    const textNode = Array.from(this.content.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    if (!textNode) return [];

    const range = document.createRange();
    range.selectNodeContents(textNode);
    const rects = Array.from(range.getClientRects());
    range.detach();

    return rects;
  }

  getLineCount(textRects, buttonRect, lineHeight) {
    const tolerance = lineHeight * 0.4;
    const lineTops = [...textRects.map((rect) => rect.top), buttonRect.top].sort((a, b) => a - b);

    return lineTops.reduce((count, top) => {
      const previousTop = lineTops.slice(0, count).find((lineTop) => Math.abs(lineTop - top) <= tolerance);

      return previousTop === undefined ? count + 1 : count;
    }, 0);
  }
}

if (!customElements.get('author-article-filters')) {
  customElements.define('author-article-filters', AuthorArticleFilters);
}

if (!customElements.get('author-bio-toggle')) {
  customElements.define('author-bio-toggle', AuthorBioToggle);
}
